import React, { useCallback, useEffect, useState } from 'react';
import ContractsTable from './ContractsTable';
import ContractModal from './ContractModal';
import SellerSelect from './SellerSelect';
import { TYPE_OPTIONS, isoDatePlusDays, inputClass, labelClass, primaryButton } from './contractLabels';
import { getUserContracts, createContract, errorMessage } from '../../services/contracts/contractsService';

const emptyForm = () => ({
    sensorId: '', type: 'PURCHASE', startDate: isoDatePlusDays(0), validUntil: '', sellerEmail: '', notes: '',
});

// Contratos de un usuario dentro de su ficha. onChanged avisa al formulario para recargar sus sensores
const UserContractsSection = ({ userEmail, sensors, onChanged }) => {
    const [contracts, setContracts] = useState([]);
    const [showClosed, setShowClosed] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState(emptyForm());
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        try {
            setContracts(await getUserContracts(userEmail));
        } catch (e) {
            console.error('Error al cargar los contratos: ', e);
        }
    }, [userEmail]);

    useEffect(() => { load(); }, [load]);

    const handleChanged = () => {
        load();
        onChanged && onChanged();
    };

    const handleCreate = async () => {
        setBusy(true);
        setError(null);
        try {
            await createContract({
                ...form,
                userEmail,
                validUntil: form.validUntil || null,
                startDate: form.startDate || null,
            });
            setModalOpen(false);
            handleChanged();
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    const visible = showClosed ? contracts : contracts.filter((c) => c.status === 'ACTIVE' || c.status === 'SUSPENDED');
    const closedCount = contracts.length - contracts.filter((c) => c.status === 'ACTIVE' || c.status === 'SUSPENDED').length;

    return (
        <div className="w-full">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <h2 className="text-xl font-semibold text-blue-600">Contratos</h2>
                <div className="flex items-center gap-4">
                    {closedCount > 0 && (
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />
                            Ver historial ({closedCount})
                        </label>
                    )}
                    <button type="button" className={primaryButton}
                        onClick={() => { setForm(emptyForm()); setError(null); setModalOpen(true); }}>
                        <span className="material-icons-outlined mr-1 text-base">add</span> Nuevo contrato
                    </button>
                </div>
            </div>
            <p className="text-xs text-gray-600 mb-2">
                Los sensores marcados arriba se guardan como compra sin vencimiento. Usa esta sección para alquileres,
                fechas de pago y préstamos. Los cambios aquí se aplican de inmediato.
            </p>

            <ContractsTable contracts={visible} showUser={false} sensors={sensors} onChanged={handleChanged}
                emptyText="Este usuario no tiene contratos abiertos" />

            {modalOpen && (
                <ContractModal title="Nuevo contrato" onClose={() => setModalOpen(false)} onConfirm={handleCreate}
                    busy={busy} error={error} confirmLabel="Crear contrato">
                    <div>
                        <label className={labelClass} htmlFor="contractSensor">Sensor</label>
                        <select id="contractSensor" className={inputClass} required value={form.sensorId}
                            onChange={(e) => setForm({ ...form, sensorId: e.target.value })}>
                            <option value="" disabled>Selecciona un sensor</option>
                            {sensors.map((s) => (
                                <option key={s.id} value={s.id}>{s.id}{s.name ? ` · ${s.name}` : ''}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className={labelClass} htmlFor="contractType">Tipo</label>
                        <select id="contractType" className={inputClass} value={form.type}
                            onChange={(e) => setForm({ ...form, type: e.target.value })}>
                            {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        {form.type === 'TRIAL' && (
                            <p className="text-xs text-gray-600 mt-1">Solo se pueden prestar unidades de préstamo disponibles.</p>
                        )}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-1">
                            <label className={labelClass} htmlFor="contractStart">Inicio</label>
                            <input id="contractStart" type="date" className={inputClass} value={form.startDate}
                                onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
                        </div>
                        <div className="flex-1">
                            <label className={labelClass} htmlFor="contractUntil">
                                {form.type === 'TRIAL' ? 'Prueba hasta' : 'Pagado hasta'}
                            </label>
                            <input id="contractUntil" type="date" className={inputClass} value={form.validUntil}
                                required={form.type === 'TRIAL'}
                                onChange={(e) => setForm({ ...form, validUntil: e.target.value })} />
                            {form.type !== 'TRIAL' && <p className="text-xs text-gray-600 mt-1">Vacío = sin vencimiento.</p>}
                        </div>
                    </div>
                    <div>
                        <label className={labelClass} htmlFor="contractSeller">Vendedor (opcional)</label>
                        <SellerSelect id="contractSeller" value={form.sellerEmail}
                            onChange={(sellerEmail) => setForm({ ...form, sellerEmail })} />
                    </div>
                    <div>
                        <label className={labelClass} htmlFor="contractNotes">Notas</label>
                        <input id="contractNotes" type="text" className={inputClass} value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                    </div>
                </ContractModal>
            )}
        </div>
    );
};

export default UserContractsSection;
